import { useEffect, useState } from "react";
import { DateRangePicker } from "react-dates";
import { ArrowLeft, ArrowRight, Calendar, X } from "react-feather";
import Popup from "../../components/Popup";
import Button from "../../components/Buttons/Button";
import PopupMenu from "../../components/PopupMenu";
import ButtonGroupWrapper from "../../components/Buttons/ButtonGroupWrapper";
import moment from "moment"
import Divider from "../../components/Divider";
import "react-dates/initialize";
import "react-dates/lib/css/_datepicker.css";
import "../../styles/DateRangeOverrides.scss";


const DateRangeInput = ({ onChange, value, showDateRangeButton, dateRanges, inceptionDate }) => {
	const [focusedInput, setFocusedInput] = useState(null)
	const [from, setFrom] = useState(value?.from)
	const [activePreset, setActivePreset] = useState("")
	const [to, setTo] = useState(value?.to)
	const [datePairs, setDatePairs] = useState([])
	const [labels, setLabels] = useState([])
	const [wholeMonths, setWholeMonths] = useState("");
	const [currentDisplayMonth, setCurrentDisplayMonth] = useState(moment());

	useEffect(() => {
		if (dateRanges) {
			const defaultRange = dateRanges[0]
			const entries = Object.entries(defaultRange);
			const label = `${entries[0][1]} ${entries[0][0]}`;
			presetDateSelected([entries[0][1], entries[0][0]])
			setActivePreset(label)
		}
		const ranges = dateRanges ? dateRanges : [{ months: 1 }, { months: 3 }, { years: 1 }];
		let rangesLabels = []
		const pairs = ranges.map((dr) => {
			const entries = Object.entries(dr);
			const correctedLabel = entries[0][1] > 1 ? entries[0][0] : entries[0][0].slice(0, -1);
			rangesLabels.push(`${entries[0][1]} ${correctedLabel}`);
			const toDate = moment()
			const fromDate = moment().subtract(entries[0][1], entries[0][0]);
			const end = moment(toDate).format('YYYY-MM-DD');
			const start = moment(fromDate).format('YYYY-MM-DD');
			return `from: ${start}, to: ${end}`
		})
		setLabels(rangesLabels);
		setDatePairs(pairs);
	}, [dateRanges])

	useEffect(() => {
		onChange({ from, to })
		checkButtonsAreActive(from, to, datePairs);
	}, [from, to])

	function presetDateSelected(timeSpan) {
		setTo(moment())
		setFrom(moment().subtract(timeSpan[0], timeSpan[1]))
	}

	function checkButtonsAreActive(fromDate, toDate, datePairs) {
		if (datePairs.length > 0 && labels.length > 0) {
			const fromStr = fromDate ? moment(fromDate).format('YYYY-MM-DD') : null;
			const toStr = toDate ? moment(toDate).format('YYYY-MM-DD') : null;
			const str = `from: ${fromStr}, to: ${toStr}`;
			const isActive = datePairs.findIndex(pair => pair === str)
			if (isActive > -1) setActivePreset(labels[isActive])
			else setActivePreset("")
		}
	}

	function handleMonthClick(month) {
		setWholeMonths(moment(month).format('MMMM YYYY'));
		const end = moment(month).endOf('month')
			.isAfter(moment()) ? moment() : moment(month).endOf('month');
		const start = moment(month).startOf('month');
		setFrom(start);
		setTo(end);
	}

	function handlePrevMonthClick() {
		const isRightCalendar = moment().isSame(currentDisplayMonth, 'month');
		const prevMonth = isRightCalendar ? currentDisplayMonth.clone().subtract(2, 'month') : currentDisplayMonth.clone().subtract(1, 'month');
		if (!isUnclickableMonth(prevMonth)) {
			setCurrentDisplayMonth(prevMonth);
		}
	};
	
	function handleNextMonthClick() {
		const isLeftCalendar = moment().subtract(2, 'month').isSame(currentDisplayMonth, 'month');
		const nextMonth = isLeftCalendar ? currentDisplayMonth.clone().add(2, 'month') : currentDisplayMonth.clone().add(1, 'month');
		if (!isUnclickableMonth(nextMonth)) {
			setCurrentDisplayMonth(nextMonth);
		}
	};

	function isUnclickableMonth(month) {
		const isAfter = moment(month).startOf('month').isAfter(moment())
		if (inceptionDate) {
			const inceptionMoment = moment(inceptionDate);
			const isBefore = moment(month).endOf('month').isBefore(inceptionMoment);
			if (isBefore || isAfter) {
				return true;
			}
		} else return isAfter;
	}

	function renderDatePresets() {
		return (
			<div className="date-range-info-outer">
				<div className="date-range-info-inner">
					<ButtonGroupWrapper gap="s">
						{dateRanges
							? dateRanges.map((dr) => {
								const entries = Object.entries(dr);
								const correctedLabel = entries[0][1] > 1 ? entries[0][0] : entries[0][0].slice(0, -1);
								const label = `${entries[0][1]} ${correctedLabel}`;
								return (
									<Button variant={activePreset === label ? "" : "outlined"} small onClick={() => { presetDateSelected([entries[0][1], entries[0][0]]); setActivePreset(label) }} >{label}</Button>
								)
							})
							: <>
								<Button variant={activePreset === "1 month" ? "" : "outlined"} small onClick={() => { presetDateSelected([1, "months"]); setActivePreset("1 month") }} >1 Month</Button>
								<Button variant={activePreset === "3 months" ? "" : "outlined"} small onClick={() => { presetDateSelected([3, "months"]); setActivePreset("3 months") }} >3 Months</Button>
								<Button variant={activePreset === "1 year" ? "" : "outlined"} small onClick={() => { presetDateSelected([1, "years"]); setActivePreset("1 year") }} >1 Year</Button>
							</>
						}
						<Divider vertical />
						<Button variant="secondary" small onClick={() => { setTo(null); setFrom(null); setActivePreset("") }}>Clear</Button>
					</ButtonGroupWrapper>
				</div>
			</div>
		)
	}

	const isPrevMonthClickable = !isUnclickableMonth(currentDisplayMonth.clone().subtract(1, 'month'));
	const isNextMonthClickable = !isUnclickableMonth(currentDisplayMonth.clone().add(1, 'month'));


	return (
		<div className="date-range-container">
			{showDateRangeButton && (
				<Popup
					className="display-xs-none display-lg-block"
					absolute
					popupOuterWidth="100%"
					triggerElement={<Button variant="secondary">Date Range</Button>}
					popupElement={<PopupMenu menuItems={["Last 28 Days", "1 Month", "3 Months", "1 Year"]} />}
				/>
			)}
			<div className={`calendar-range-wrapper${!isPrevMonthClickable ? ' hide-prev-nav' : ''}${!isNextMonthClickable ? ' hide-next-nav' : ''}`}>
				<DateRangePicker
					startDate={from} // momentPropTypes.momentObj or null,
					startDateId="date-range-picker-start" // PropTypes.string.isRequired,
					endDate={to} // momentPropTypes.momentObj or null,
					endDateId="date-range-picker-end" // PropTypes.string.isRequired,
					onDatesChange={({ startDate, endDate }) => { setFrom(startDate); setTo(endDate) }} // PropTypes.func.isRequired,
					focusedInput={focusedInput} // PropTypes.oneOf([START_DATE, END_DATE]) or null,
					onFocusChange={(focusedInput) => setFocusedInput(focusedInput)} // PropTypes.func.isRequired,
					displayFormat={() => "DD/MM/YYYY"}
					hideKeyboardShortcutsPanel
					noBorder
					verticalSpacing={5}
					keepOpenOnDateSelect
					isOutsideRange={(day) => {
						const monthStart = moment(day).startOf('month');
						return isUnclickableMonth(monthStart);
					}}
					onPrevMonthClick={handlePrevMonthClick}
					onNextMonthClick={handleNextMonthClick}
					navPrev={
						<div className="navigation-button navigation-button__prev">
							<ArrowLeft />
						</div>
					}
					navNext={
						<div className="navigation-button navigation-button__next">
							<ArrowRight />
						</div>
					}
					customArrowIcon={<div className={`arrow-icon ${from && from.isValid ? "start-date-valid" : ""}`}>—</div>}
					customInputIcon={<Calendar className="calendar-icon" />}
					renderCalendarInfo={renderDatePresets}
					renderMonthText={(month) => (
						<span
							onClick={() => isUnclickableMonth(month) ? null : handleMonthClick(month)}
							style={isUnclickableMonth(month) ? { cursor: 'default', color: '#333B5A', border: 'none', opacity: 0.3 } : null}
							className={`clickable-month-header${wholeMonths === moment(month).format('MMMM YYYY') ? ' active' : ''}`}
						>
							{moment(month).format('MMMM YYYY')}
						</span>
					)}
				/>
				{(from || to) && <X className="transition-fade-in clear-icon" onClick={() => { setTo(null); setFrom(null); setActivePreset("") }} />}
			</div>
		</div>
	);

}

export default DateRangeInput;
